import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import { Stack } from '@sureco/design-system'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { SortDirection } from '../../../types/sorted-request.dto'
import { getCensusFiles } from '../api/census-enrollment-platform-api-client'
import { CensusFileWithRowStatsDto } from '../api/dto/census-file-with-row-stats.dto'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { filterClients } from '../CensusReview/utils/filter-clients'
import {
  CENSUS_MANAGEMENT_PATH,
  DEFAULT_FILES_PAGINATION_LIMIT,
} from '../constants'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { CensusClientFilter } from '../types/census-client-filter'
import { getCensusFilesColDefs } from '../utils/get-census-files-col-defs'

type Props = {
  clientNameLookup: Record<string, string>
  filter?: CensusClientFilter
}

const ActivityHistoryGrid = ({ clientNameLookup, filter }: Props) => {
  const [grid, setGrid] = useState<GridReadyEvent>()
  const { clients } = useCensusV2Clients()
  const role = useSelector(authSelectors.getRole) as string

  const clientIds = filterClients(clients, filter, role).map(
    (client) => client.id
  )

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<CensusFileWithRowStatsDto>
      ) => {
        const limit = DEFAULT_FILES_PAGINATION_LIMIT
        let sort: string | undefined
        let order: SortDirection | undefined
        try {
          const { startRow, sortModel } = params.request
          if (sortModel.length > 0) {
            const { colId, sort: direction } = sortModel[0] // Take the first sort entry
            sort = colId
            order =
              direction === 'desc' ? SortDirection.desc : SortDirection.asc
          }
          const status = [CensusFileRowStatusEnum.IN_REVIEW]

          const page = Math.floor((startRow ?? 1) / limit) + 1
          const { meta: pagination, data: files } = await getCensusFiles({
            status,
            page,
            limit,
            sort,
            order,
            clientPublicKey: clientIds,
            includeTestClients: filter?.includeTestCompanies,
            dateFrom: filter?.dateFrom,
            dateTo: filter?.dateTo,
          })

          params.success({ rowData: files, rowCount: pagination.total })
        } catch (error) {
          console.error(error)
          params.fail()
        }
      },
    }),
    [clientIds, clientNameLookup, filter]
  )

  useEffect(() => {
    if (grid) {
      grid.api.setGridOption('serverSideDatasource', datasource)
      grid.api.purgeInfiniteCache()
      grid.api.refreshServerSide({ purge: true })
    }
  }, [grid, datasource])

  const clickView = (publicKey: string) => {
    window.open(`${CENSUS_MANAGEMENT_PATH}/${publicKey}/in-review`, '_blank')
  }

  const columnDefs = getCensusFilesColDefs({
    hasSubmittedColumn: true,
    clientNameLookup,
    clickView,
    isSurecoAdmin: role === ROLE_PLATFORM_HIXME_ADMIN,
  })

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        In Review
      </Stack>
      <AgGridReact
        rowStyle={{
          backgroundColor: 'white',
          marginBottom: '20px',
        }}
        className="censusReviewGrid ag-theme-alpine"
        getRowId={({ data }) => data.id}
        pagination
        paginationPageSize={10}
        cacheBlockSize={10}
        columnDefs={columnDefs}
        rowModelType="serverSide"
        onGridReady={(params) => setGrid(params)}
        domLayout="autoHeight"
        suppressHorizontalScroll
        paginationPageSizeSelector={[10, 20, 50, 100]}
      />
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default ActivityHistoryGrid
